import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

const useSEO = (seoData: SEOData) => {
  const location = useLocation();

  useEffect(() => {
    // Set title
    document.title = seoData.title;

    // Set or update meta tags
    const setMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };

    // Basic SEO
    setMetaTag('description', seoData.description);
    if (seoData.keywords) {
      setMetaTag('keywords', seoData.keywords);
    }

    // Open Graph
    setMetaTag('og:title', seoData.title, true);
    setMetaTag('og:description', seoData.description, true);
    setMetaTag('og:type', 'website', true);
    setMetaTag('og:url', `${window.location.origin}${location.pathname}`, true);
    setMetaTag('og:locale', 'it_IT', true);
    
    if (seoData.ogImage) {
      setMetaTag('og:image', seoData.ogImage, true);
      setMetaTag('og:image:alt', seoData.title, true);
    }

    // Twitter Cards
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', seoData.title);
    setMetaTag('twitter:description', seoData.description);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seoData.canonical ?? `${window.location.origin}${location.pathname}`);

    // Hreflang tags for multilingual support
    const languages = ['it', 'en', 'es'];
    const currentUrl = `${window.location.origin}${location.pathname}`;
    
    // Remove existing hreflang tags
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(tag => tag.remove());
    
    // Add hreflang tags for each language
    languages.forEach(lang => {
      const hreflang = document.createElement('link');
      hreflang.setAttribute('rel', 'alternate');
      hreflang.setAttribute('hreflang', lang);
      hreflang.setAttribute('href', `${currentUrl}?lang=${lang}`);
      document.head.appendChild(hreflang);
    });
    
    // Add x-default hreflang (Italian as default)
    const hreflangDefault = document.createElement('link');
    hreflangDefault.setAttribute('rel', 'alternate');
    hreflangDefault.setAttribute('hreflang', 'x-default');
    hreflangDefault.setAttribute('href', currentUrl);
    document.head.appendChild(hreflangDefault);

    // Schema.org structured data
    if (seoData.schema) {
      let schemaScript = document.querySelector('#schema-org');
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        schemaScript.setAttribute('id', 'schema-org');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(seoData.schema);
    }

  }, [seoData, location.pathname]);
};

export default useSEO;
